import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { OrderRef } from "@/components/StockBadge";

// SOESO-FULFIL-01 · FG Store → new Customer PS/EPS from received Stock PS/EPS packs (Sales Only orders).
const btn = "h-8 px-3 bg-[var(--accent)] text-black text-[11px] font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 disabled:cursor-not-allowed";
const inp = "h-8 px-2 bg-[var(--bg-2)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)]";
const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || "Request failed"; };
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

function Summary({ rows }) {
  return (
    <div className="tactical-card p-3" data-testid="fg-alloc-summary">
      <div className="overline mb-2">FG Store balance · on hand / reserved / available</div>
      <table className="w-full text-xs font-mono">
        <thead><tr className="text-zinc-500 text-[10px] uppercase"><th className="text-left p-1">Product</th><th className="text-right p-1">On hand</th><th className="text-right p-1">Reserved</th><th className="text-right p-1">Available</th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.item_id} className="border-t border-[var(--border)]/40" data-testid={`fg-alloc-sum-${r.product_code}`}>
              <td className="p-1">{r.product_code}</td>
              <td className="p-1 text-right" data-testid={`fg-alloc-onhand-${r.product_code}`}>{fmt(r.on_hand)} m</td>
              <td className="p-1 text-right text-amber-300" data-testid={`fg-alloc-reserved-${r.product_code}`}>{fmt(r.reserved)} m</td>
              <td className="p-1 text-right text-emerald-300" data-testid={`fg-alloc-available-${r.product_code}`}>{fmt(r.available)} m</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="p-2 text-zinc-500">No FG stock.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PackTable({ packs, picked, toggle, line }) {
  return (
    <div className="tactical-card overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead><tr className="text-zinc-500 text-[10px] uppercase">
          <th className="p-2"></th><th className="text-left p-2">Pack</th><th className="text-left p-2">Stock PS/EPS</th><th className="text-left p-2">Stock SO/ESO</th>
          <th className="text-left p-2">WO</th><th className="text-left p-2">FG Receipt</th><th className="text-left p-2">Product</th><th className="text-right p-2">Qty</th><th className="text-left p-2">Status</th>
        </tr></thead>
        <tbody>
          {packs.map(p => {
            const ok = p.status === "Available" && (!line || !line.item_id || line.item_id === p.item_id || line.product_code === p.product_code);
            return (
              <tr key={p.pack_key} className="border-t border-[var(--border)]/40" data-testid={`fg-pack-row-${p.box_no}`}>
                <td className="p-2"><input type="checkbox" disabled={!ok} checked={picked.has(p.pack_key)} onChange={() => toggle(p.pack_key)} data-testid={`fg-pack-pick-${p.box_no}`} /></td>
                <td className="p-2">{p.box_no}</td>
                <td className="p-2">{p.source_ps_no}</td>
                <td className="p-2"><OrderRef no={p.stock_order_no} stock testId={`fg-pack-stock-ref-${p.box_no}`} /></td>
                <td className="p-2 text-zinc-400">{p.source_wo_no || "—"}</td>
                <td className="p-2 text-zinc-400">{p.fgtr_no}</td>
                <td className="p-2">{p.product_code}</td>
                <td className="p-2 text-right">{fmt(p.qty)} m</td>
                <td className="p-2" data-testid={`fg-pack-status-${p.box_no}`}>{p.status}{p.customer_slip_no ? ` · ${p.customer_slip_no}` : ""}</td>
              </tr>
            );
          })}
          {packs.length === 0 && <tr><td colSpan={9} className="p-3 text-zinc-500">No packs received from Stock PS/EPS.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function FGCustomerSlip() {
  const [ot, setOt] = useState("SO");
  const [orders, setOrders] = useState([]);
  const [packs, setPacks] = useState([]);
  const [summary, setSummary] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [lineId, setLineId] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [reqKey, setReqKey] = useState(newKey);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [o, p, s] = await Promise.all([
      api.get("/fg-stock/eligible-orders", { params: { order_type: ot } }),
      api.get("/fg-stock/packs", { params: { order_type: ot } }),
      api.get("/fg-stock/summary"),
    ]);
    setOrders(o.data || []); setPacks(p.data || []); setSummary(s.data || []);
  }, [ot]);
  useEffect(() => { load().catch(e => toast.error(errText(e))); setOrderId(""); setLineId(""); setPicked(new Set()); }, [load]);

  const order = orders.find(o => o.id === orderId);
  const line = order?.lines.find(l => l.line_id === lineId);
  const pickedQty = useMemo(() => packs.filter(p => picked.has(p.pack_key)).reduce((a, p) => a + p.qty, 0), [packs, picked]);
  const toggle = (k) => setPicked(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fg-stock/customer-slip", { order_type: ot, order_id: orderId, line_id: lineId, pack_keys: [...picked], request_key: reqKey });
      toast.success(`${data.slip_no} created from FG Stock`);
      setPicked(new Set()); setReqKey(newKey()); setOrderId(""); setLineId("");
      await load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="fg-customer-slip-panel">
      <div className="tactical-card p-3 flex items-end gap-2 flex-wrap">
        <div className="flex gap-1">
          {[["SO", "Domestic · Customer PS"], ["ESO", "Export · Customer EPS"]].map(([k, l]) => (
            <button key={k} data-testid={`fg-alloc-type-${k}`} onClick={() => setOt(k)}
              className={`h-8 px-3 text-[11px] font-mono uppercase tracking-widest rounded-sm ${ot === k ? "bg-[var(--accent)] text-black" : "border border-[var(--border)] text-zinc-300"}`}>{l}</button>
          ))}
        </div>
        <label className="text-[10px] uppercase text-zinc-500">Sales Only {ot}
          <select data-testid="fg-alloc-order" value={orderId} onChange={e => { setOrderId(e.target.value); setLineId(""); setPicked(new Set()); }} className={`${inp} block min-w-[220px]`}>
            <option value="">Select order…</option>
            {orders.map(o => <option key={o.id} value={o.id}>{o.order_no} — {o.customer || "—"}</option>)}
          </select>
        </label>
        <label className="text-[10px] uppercase text-zinc-500">Order line
          <select data-testid="fg-alloc-line" value={lineId} onChange={e => { setLineId(e.target.value); setPicked(new Set()); }} className={`${inp} block min-w-[260px]`} disabled={!order}>
            <option value="">Select line…</option>
            {(order?.lines || []).map(l => <option key={l.line_id} value={l.line_id}>{l.product_code} · remaining {fmt(l.remaining_qty)} m</option>)}
          </select>
        </label>
        <div className="text-[11px] font-mono text-zinc-400" data-testid="fg-alloc-picked">Picked {picked.size} · {fmt(pickedQty)} m{line ? ` / remaining ${fmt(line.remaining_qty)} m` : ""}</div>
        <button data-testid="fg-alloc-create" className={`${btn} ml-auto`} onClick={create}
          disabled={busy || !orderId || !lineId || picked.size === 0 || (line && pickedQty > line.remaining_qty + 1e-6)}>
          Create Customer {ot === "ESO" ? "EPS" : "PS"}
        </button>
      </div>
      <Summary rows={summary} />
      <PackTable packs={packs} picked={picked} toggle={toggle} line={line} />
    </div>
  );
}
